import { memo, forwardRef, useCallback, useEffect, useRef } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { cn } from '@/libs/utils';
import { isFunction } from '@/utils/utils';
import { Aside } from '../layouts';

const sidebarStack = [];

const scrollToTopByElement = (element) => {
  if (element) {
    element.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
};

function SidebarHeader({ onClose, title, headerActions }) {
  return (
    <header className="flex h-[var(--headerHeight)] w-full items-center justify-between gap-2 bg-accent px-4">
      <div className="flex items-center gap-2">
        <Button
          type="default"
          className="btn-default h-8 w-8 rounded-md bg-accent p-0 text-white hover:!bg-black/10 hover:!text-white"
          onClick={onClose}
          icon={<CloseOutlined style={{ fontSize: '18px' }} />}
        >
          <span className="sr-only">{title}</span>
        </Button>

        <h4 className="text-lg font-semibold text-white">{title}</h4>
      </div>

      {headerActions && (
        <div className="flex items-center gap-2">{headerActions}</div>
      )}
    </header>
  );
}

const SidebarContent = forwardRef(({ children }, ref) => {
  return (
    <div className="relative flex min-h-[calc(100svh_-_var(--headerHeight))]">
      <div
        ref={ref}
        className="absolute inset-0 m-2 overflow-y-auto rounded-lg bg-white"
      >
        {children}
      </div>
    </div>
  );
});

SidebarContent.displayName = 'SidebarContent';

const RenderSidebarContent = memo(({ children, open, onClose, onScroll }) => {
  return isFunction(children)
    ? children({ open, onClose, onScroll })
    : children;
});

RenderSidebarContent.displayName = 'RenderSidebarContent';

const Sidebar = forwardRef(
  (
    {
      open = false,
      onClose,
      placement = 'right',
      className,
      title,
      headerActions,
      children,
    },
    ref
  ) => {
    const scrollRef = useRef(null);

    const positionClasses = {
      left: 'left-0 top-0 h-full rounded-tr-lg rounded-br-lg',
      right: 'right-0 top-0 h-full rounded-tl-lg rounded-bl-lg',
    };

    const overlayClasses = {
      'opacity-100': open,
      'pointer-events-none opacity-0': !open,
    };

    const sidebarClasses = {
      'translate-x-0': open && (placement === 'left' || placement === 'right'),
      '-translate-x-full': !open && placement === 'left',
      'translate-x-full': !open && placement === 'right',
    };

    const handleEscape = useCallback((event) => {
      if (event.key === 'Escape') {
        const lastSidebar = sidebarStack.pop();

        if (lastSidebar) {
          lastSidebar.onClose();
        }
      }
    }, []);

    useEffect(() => {
      if (!open) {
        setTimeout(() => {
          document.activeElement?.blur();
        }, 50);
      }
    }, [open]);

    const onScroll = useCallback(() => {
      if (scrollRef?.current) {
        setTimeout(() => {
          scrollToTopByElement(scrollRef.current);
        }, 50);
      }
    }, [scrollRef]);

    useEffect(() => {
      if (open) {
        sidebarStack.push({ onClose });
      } else {
        const index = sidebarStack.findIndex(
          (item) => item.onClose === onClose
        );

        if (index !== -1) {
          sidebarStack.splice(index, 1);
        }
      }

      return () => {
        const index = sidebarStack.findIndex(
          (item) => item.onClose === onClose
        );
        if (index !== -1) {
          sidebarStack.splice(index, 1);
        }
      };
    }, [open, onClose]);

    useEffect(() => {
      if (!open && sidebarStack.length < 1) {
        onScroll();
      }
    }, [open, onScroll]);

    useEffect(() => {
      if (open) {
        document.addEventListener('keydown', handleEscape);
      } else {
        document.removeEventListener('keydown', handleEscape);
      }

      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }, [open, handleEscape]);

    return (
      <>
        <div
          onClick={onClose}
          className={cn(
            'fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity duration-150',
            overlayClasses
          )}
          style={{ willChange: 'transform, opacity' }}
        />

        <Aside
          ref={ref}
          role="aside"
          className={cn(
            'fixed z-50 grid w-full max-w-md grow overflow-hidden bg-accent shadow-lg transition-transform duration-300',
            positionClasses[placement],
            sidebarClasses,
            className
          )}
          style={{ willChange: 'transform, opacity' }}
        >
          <SidebarHeader
            onClose={onClose}
            title={title}
            headerActions={headerActions}
          />

          <SidebarContent ref={scrollRef}>
            <RenderSidebarContent
              open={open}
              onClose={onClose}
              onScroll={onScroll}
            >
              {children}
            </RenderSidebarContent>
          </SidebarContent>
        </Aside>
      </>
    );
  }
);

Sidebar.displayName = 'Sidebar';

export default Sidebar;
